import { FileVideo, Image, Upload } from "lucide-react";
import type { CropPosition, MediaAspectRatio, MediaType } from "../lib/types";
import { validateMedia } from "../lib/validators";

interface MediaUploaderProps {
  mediaType: MediaType;
  file: File | null;
  aspectRatio: MediaAspectRatio;
  cropPosition: CropPosition;
  onFileChange: (file: File | null) => void;
  onAspectRatioChange: (aspectRatio: MediaAspectRatio) => void;
  onCropPositionChange: (cropPosition: CropPosition) => void;
}

const acceptByType: Record<MediaType, string> = {
  photo: "image/jpeg,image/png,image/webp",
  animation: "image/gif",
  video: "video/mp4"
};

const aspectRatioOptions: MediaAspectRatio[] = ["original", "1:1", "4:5", "2:3", "9:16", "16:9"];
const cropPositionOptions: CropPosition[] = ["center", "top", "bottom"];

export function MediaUploader({
  mediaType,
  file,
  aspectRatio,
  cropPosition,
  onFileChange,
  onAspectRatioChange,
  onCropPositionChange
}: MediaUploaderProps) {
  const error = file ? validateMedia(file, mediaType) : null;
  const Icon = mediaType === "video" ? FileVideo : Image;

  return (
    <div className="space-y-4">
      <label className="block rounded border border-dashed border-line bg-white p-4 transition hover:border-brand">
        <input
          type="file"
          accept={acceptByType[mediaType]}
          className="sr-only"
          onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
        />
        <span className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded bg-brand/10 text-brand">
            {file ? <Icon size={22} /> : <Upload size={22} />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-ink">{file ? file.name : "เลือกไฟล์จากเครื่อง"}</span>
            <span className="block text-xs text-slate-500">
              {mediaType === "photo" ? "JPG, PNG, WEBP" : mediaType === "animation" ? "GIF" : "MP4"} สูงสุด 50MB
            </span>
          </span>
        </span>
        {error ? <span className="mt-2 block text-sm text-red-600">{error}</span> : null}
      </label>

      <div className="grid gap-3 md:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Aspect Ratio</span>
          <select
            value={aspectRatio}
            onChange={(event) => onAspectRatioChange(event.target.value as MediaAspectRatio)}
            className="focus-ring w-full rounded border border-line bg-white px-3 py-2 text-sm"
          >
            {aspectRatioOptions.map((option) => (
              <option key={option} value={option}>
                {option === "original" ? "Original" : option}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Crop Position</span>
          <select
            value={cropPosition}
            onChange={(event) => onCropPositionChange(event.target.value as CropPosition)}
            disabled={aspectRatio === "original"}
            className="focus-ring w-full rounded border border-line bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400"
          >
            {cropPositionOptions.map((option) => (
              <option key={option} value={option}>
                {option[0].toUpperCase()}
                {option.slice(1)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {mediaType !== "photo" && aspectRatio !== "original" ? (
        <p className="text-xs text-slate-500">
          TODO: crop จริงสำหรับ GIF/MP4 ควรทำด้วย transcoding ฝั่ง worker/storage flow ตอนนี้ crop ใช้กับ preview และส่งไฟล์ต้นฉบับสำหรับ GIF/MP4
        </p>
      ) : null}
    </div>
  );
}
